import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostListener, Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {APIS, DEFAULT_CONSTANTS, MESSAGES, USER_DETAILS} from 'app/common/constants';
import {HttpService} from 'app/core/services/http.service';
import {UserService} from 'app/core/services/user.service';
import {Subject} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import Swal from 'sweetalert2';
import {AlertService} from 'app/core/services/alert.service';
import {SkipReason, SkippedInspection} from "./skipped-inspection.model";


@Component({
  selector: 'app-skipped-inspection',
  templateUrl: './skipped-inspections.component.html',
  styleUrls: ['./skipped-inspections.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SkippedInspectionsComponent implements OnInit, OnDestroy {
  isClientView = false;
  @Input('isClientView') set clientView(val: boolean) {
    this.isClientView = val;
    this.isClientView && this.getSkipReasons(true)
    this.cdr.detectChanges();
  }
  title: string = 'Create'
  consoleCenters: any;
  text = MESSAGES;
  isAdmin: boolean = false;
  selectedConsoleCenter: string;
  searchProductCode: any = '';
  searchPOLine: any = '';
  queryParams = {};
  page: number = 1;
  limit: number = 10;
  totalCounts: number;
  isNextClicked = false;
  isLoading = false;
  destroy$ = new Subject();
  timeouts: Map<string, NodeJS.Timeout> = new Map()
  paginationControls = {
    page: 1,
    limit: 10
  }
  isTab = false;
  isMobile = false;
  skippedRecords: SkippedInspection[] = null;
  skipReasons: SkipReason[] = null;
  selectedSkipReason = null;
  defaultConsoleCentre = '';
  startDate: string;
  endDate: string
  allSkipReasons: SkipReason[] = null;
  filterSkipReasons: SkipReason[] = null;

  constructor(private router: Router,
              private httpService: HttpService,
              private userService: UserService,
              private route: ActivatedRoute,
              private alertService: AlertService,
              private cdr: ChangeDetectorRef
  ) {
  }

  @HostListener('window:resize', [])
  onResize(): void {
    this.checkScreenSize();
  }

  private checkScreenSize(): void {
    const width = window.innerWidth;
    this.isTab = width <= 950;
    this.isMobile = width <= 600;
  }

  ngOnInit(): void {
    this.checkScreenSize();
    //Fetching the filter states from the url and setting them in local states.
    this.isAdmin = parseInt(localStorage.getItem(USER_DETAILS.ROLE)) === 1 ? false : true
    this.defaultConsoleCentre = localStorage.getItem(USER_DETAILS.LOCATION)
    this.setQueryParams()
    this.cdr.markForCheck();
    this.router.navigate([], {
      queryParams: this.queryParams,
    });

    this.route.queryParams.pipe(takeUntil(this.destroy$),
    ).subscribe((param) => {
      if (Object.keys(param).length) {
        this.fetchData();
      }
    });

    this.getConsoleCentres();
    !this.isClientView && this.getSkipReasons();

  }

  setDefaultDates() {
    const today = new Date();
    const currentDay = today.getDay();
    const mondayOffset = (currentDay === 0 ? -6 : 1) - currentDay;
    const sundayOffset = 7 - currentDay;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);

    const sunday = new Date(today);
    sunday.setDate(today.getDate() + sundayOffset);
    this.startDate = monday.toISOString().split('T')[0];
    this.endDate = sunday.toISOString().split('T')[0];
    this.queryParams['start_date'] = this.startDate;
    this.queryParams['end_date'] = this.endDate;
  }

  onDateChange() {
    this.handleSearchFilter(null, [this.startDate, this.endDate], 1000, true)
  }

  setQueryParams() {
    this.selectedConsoleCenter = this.route.snapshot.queryParamMap.get('console_centre') ?? this.defaultConsoleCentre;
    this.searchProductCode = this.route.snapshot.queryParamMap.get('product_code') || '';
    this.searchPOLine = this.route.snapshot.queryParamMap.get('po_num_line_num') || '';
    this.searchProductCode !== "" && (this.queryParams['product_code'] = this.searchProductCode);
    this.searchPOLine !== "" && (this.queryParams['po_num_line_num'] = this.searchPOLine)
    this.queryParams['console_centre'] = this.selectedConsoleCenter
    const skipReasonParam = this.route.snapshot.queryParamMap.get('skip_reason')
    if (skipReasonParam) {
      this.queryParams['skip_reason'] = skipReasonParam;
      this.selectedSkipReason = skipReasonParam
    }
    this.startDate = this.route.snapshot.queryParamMap.get('start_date')
    this.endDate = this.route.snapshot.queryParamMap.get('end_date')
    if (!this.startDate || !this.endDate) {
      this.setDefaultDates();
    } else {
      this.queryParams['start_date'] = this.startDate;
      this.queryParams['end_date'] = this.endDate;
    }

  }

  getConsoleCentres() {
    this.httpService.getDataWithCache(APIS.CONSOLE_CENTERS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.consoleCenters = res.data.map(c => {
          return {id: c.udc_id, value: c.udc_id};
        });
      });
  }

  getSkipReasons(filterOne: boolean = false) {
    this.httpService.getDataWithCache(APIS.SKIP_REASONS)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        if (filterOne) {
          this.skipReasons = [
            {id: null, description: 'Select Reason'},
            ...(res.data.filter(f => f.description.includes('RI')))]
          this.allSkipReasons = res.data;
          this.filterSkipReasons = [{id: 'null', description: 'Not Specified'},...this.skipReasons]
        } else {
          this.skipReasons = [{id: null, description: 'Select Reason'}, ...res.data];
        }
        this.cdr.markForCheck();
      })
  }

  getSkipReasonDescription(skipReasonId: string): string {
    return this.allSkipReasons?.
    find(d => d.id === skipReasonId)?.
      description ?? 'Not Selected!';
  }

  handleDebounce(operation, time, id = '') {
    const existingTimeout = this.timeouts.get(id);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    const timeout = setTimeout(() => {
      operation();
    }, time);

    this.timeouts.set(id, timeout);
  }

  clearFilters() {
    this.queryParams = {}
    this.searchProductCode = null;
    this.searchPOLine = null;
    this.selectedConsoleCenter = this.defaultConsoleCentre;
    this.selectedSkipReason = null;
    this.queryParams['console_centre'] = this.defaultConsoleCentre;
    this.setDefaultDates();
    this.router.navigate([], {
      queryParams: this.queryParams,
    }).then(r => null);
  }

  fetchData() {
    let data = {...this.queryParams};
    data['console_centre'] = this.selectedConsoleCenter;

    Swal.fire('Please wait');
    Swal.showLoading();

    this.httpService.getData(APIS.GET_OR_UPDATE_SKIPPED_INSPECTIONS, data)
      .pipe(takeUntil(this.destroy$))
      .subscribe(res => {
        this.skippedRecords = res.data;
        this.totalCounts = res.data?.length;
        this.isLoading = false;
        Swal.close();
        this.cdr.markForCheck();
      }, (error) => {
        this.alertService.errorToast(
          error.msg ??
          error.error?.msg ??
          error.message
        );
        this.clearFilters();
        Swal.close();

      }, () => {
        this.isLoading = false;
        Swal.close();
      });
  }


  handleDropdownFilters(state, param) {
    this.page = 1;
    if (state) {
      this.queryParams[param] = state;
    } else {
      delete this.queryParams[param];
    }
    this.router.navigate([], {
      queryParams: this.queryParams,
    });
  }

  selectConsoleCenter() {
    this.handleDropdownFilters(this.selectedConsoleCenter, 'console_centre')
  }

  handleSearchFilter(state, param, time, isDateFilter = false) {
    this.page = 1;
    const id = isDateFilter ? 'dateFilter' : param;
    const params = isDateFilter ? [...param] : param;
    this.handleDebounce(() => {
      if (isDateFilter) {
        if (params[0] > params[1]) return this.alertService.errorToast('Forecast from date cannot be less then to date')
        this.queryParams['start_date'] = params[0];
        this.queryParams['end_date'] = params[1];
      } else {
        if (state) {
          this.queryParams[params] = state;
        } else {
          delete this.queryParams[params];
        }
      }

      this.router.navigate([], {
        queryParams: this.queryParams,
      });
    }, time, id)
  }

  searchProductCodes() {
    this.handleSearchFilter(this.searchProductCode, 'product_code', 700);
  }

  searchPOLineNo() {
    this.handleSearchFilter(this.searchPOLine, 'po_num_line_num', 700);
  }

  nextPage(page: any) {
    this.page = page;
    this.isNextClicked = true;
  }

  submitSkipReason(data: { inspectionId: string, skipReasonId: string }) {
    Swal.fire('Updating Skip Reasons..');
    Swal.showLoading();
    const payload = {
      inspection_id: data.inspectionId,
      skip_reason_id: data.skipReasonId
    }
    this.httpService.patchData(APIS.GET_OR_UPDATE_SKIPPED_INSPECTIONS, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res) => {
          this.alertService.successToast(res.msg);
          this.fetchData()
          Swal.close();
          this.cdr.markForCheck();
        },
        (error) => {
          Swal.close();
          this.alertService.errorToast(error.msg ??
            error.error?.msg ??
            error.message);
        }
      );
  }

  onSkipReasonChange(skipReasonId: string, inspectionId: string) {
    if (!skipReasonId || !inspectionId) return
    this.submitSkipReason({inspectionId, skipReasonId})
  }

  onReasonFilterChange() {
    this.handleDropdownFilters(this.selectedSkipReason, 'skip_reason');
  }

  ngOnDestroy(): void {
    this.destroy$.next(null);
    this.destroy$.complete();
  }
}
